import { Table, TableBody, TableCell, TableContainer, TableRow, Typography } from "@mui/material";
import { IPermissionsConfig, useAddOrUpdateGroupPermissionsMutation, useDeleteGroupPermissionsMutation } from "../../../shared/api/PermissionsApi";
import TableToolbar, { ITableToolbarButton, ITableToolbarSearch } from "../../shared/table/TableToolbar";
import PermissionsRow from "./PermissionsRow";
import { GroupAdd, SearchOff } from "@mui/icons-material";
import { IAdvancedModalOptions, useModalWindowContext } from "../../ModalWindow";
import { useToastAlertContext } from "../../ToastAlert";
import { useCallback, useEffect, useMemo, useState } from "react";
import { generateDummyPermissions } from "../../../shared/types/shared/auth/userDataTypes";
import PermissionsNewGroupForm from "./PermissionsNewGroupForm";
import { usePortalContext } from "../../PortalWrapper";

const GroupPermissionsTab: React.FC<IGroupPermissionsTabProps> = ({ permissions, inputDisabled, reload }) => {
    const { openModal, updateModal } = useModalWindowContext();
    const { popToast } = useToastAlertContext();

    const [addGroup] = useAddOrUpdateGroupPermissionsMutation();
    const [savePermissions] = useAddOrUpdateGroupPermissionsMutation();
    const [deleteGroup] = useDeleteGroupPermissionsMutation();
    const { allGroups } = usePortalContext();

    const [filteredPermissions, setFilteredPermissions] = useState<IPermissionsConfig[]>();
    const [searchTerm, setSearchTerm] = useState<string>("");

    const existingGroups = useMemo(() => {
        return permissions.map((v) => v.id);
    }, [permissions]);

    const searchBar: ITableToolbarSearch = useMemo(() => {
        return {
            label: "Search Groups",
            valueUpdated: (searchTerm) => { setSearchTerm(searchTerm); }
        }
    }, []);

    const [formData, setFormData] = useState<string>("");
    const [shouldAdd, setShouldAdd] = useState(false);
    const formUpdated = useCallback((formData: string) => {
        setFormData(formData);
    }, []);

    const validityChanged = useCallback((valid: boolean) => {
        updateModal({ canSubmit: valid });
    }, [updateModal]);

    const openAddGroupModal = async () => {
        const modalOptions: IAdvancedModalOptions = {
            title: "Add Group",
            fullWidth: true,
            yesText: "Save",
            noText: "Cancel",
            contentComponent: <PermissionsNewGroupForm existingGroups={existingGroups} allGroups={allGroups || []} formUpdated={formUpdated} validityChanged={validityChanged} />,
            canSubmit: false,
        }

        const addUser = await openModal(modalOptions);

        if (addUser) {
            setShouldAdd(true);
        }
    }

    useEffect(() => {
        if (shouldAdd) {
            const permissions = generateDummyPermissions(formData);

            addGroup(permissions)
                .then(() => {
                    reload();
                })
                .catch(() => {
                    popToast(`Failed to add "${formData}"`, "error");
                })
                .finally(() => {
                    setShouldAdd(false);
                    setFormData("");
                });
        }
    }, [formData, shouldAdd, reload, popToast]);

    useEffect(() => {
        if (!searchTerm) {
            setFilteredPermissions(permissions);
        } else {
            const filtered = permissions.filter((p) => {
                return p.id.toLowerCase().includes(searchTerm.toLowerCase());
            });

            setFilteredPermissions(filtered);
        }
    }, [searchTerm, permissions]);

    const deleteConfig = async (username: string) => {
        await deleteGroup(username);
        reload();
    }

    const toolbarButtons: ITableToolbarButton[] = [
        { tooltipText: "Add Group", icon: <GroupAdd />, action: openAddGroupModal, disabled: inputDisabled }
    ];

    return (
        <>
            <TableToolbar searchBar={searchBar} buttons={toolbarButtons} />
            <TableContainer>
                <Table>
                    <TableBody>
                        {filteredPermissions?.map((value) => (
                            <PermissionsRow key={value.id} permissions={value} saveConfig={savePermissions} deleteConfig={deleteConfig} reload={reload} />
                        ))}
                        {filteredPermissions && filteredPermissions.length === 0 && (
                            <TableRow>
                                <TableCell align="center" sx={{ border: "none" }}>
                                    <SearchOff fontSize="large" />
                                    <br />
                                    <Typography variant="h6">No Groups Found</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}

export default GroupPermissionsTab;

export interface IGroupPermissionsTabProps {
    permissions: IPermissionsConfig[];
    inputDisabled?: boolean;
    reload: () => void;
}

